<template>
  <div v-if="isCheckingAuth || isLoggingOut" class="flex items-center justify-center h-screen">
    <CustomSpinner />
    <div v-if="isLoggingOut" class="ml-3 text-gray-600">Logging out...</div>
  </div>
  <div v-else-if="isAuthenticated" class="flex h-screen overflow-hidden">
    <LayoutSidebar 
      :navigators="navigators" 
      v-model:collapsed="isCollapsed"
    />
    <div class="flex min-h-0 flex-1 flex-col min-w-0 pt-16 lg:pt-0">
      <AppDashboardHeader />
      <ScrollArea class="min-h-0 flex-1">
        <div class="space-y-2 ">
          <RouterView/>
        </div>
      </ScrollArea>
    </div>
  </div>
  <div v-else class="flex items-center justify-center h-screen">
    <CustomSpinner />
    <div class="ml-3 text-gray-600">Redirecting to login...</div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeMount, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import LayoutSidebar from '@/core/views/layouts/base/components/LayoutSidebar.vue'
import { NavigationModel } from '@/types/models/Navigation.ts'
import CustomSpinner from '@/shared/components/CustomSpinner.vue'
import { useMainStore } from '@/core/store/main'
import { useAuthStore } from '@/core/auth/auth'
import { ScrollArea } from '@/shared/components/ui/scroll-area'
import AppDashboardHeader from '@/core/views/layouts/base/components/AppDashboardHeader.vue';

const router = useRouter()
const isCollapsed = ref(false)

watch(isCollapsed, (collapsed) => {
  if (typeof window !== 'undefined') {
    document.documentElement.style.setProperty('--sidebar-width', collapsed ? '3.5rem' : '16rem')
  }
}, { immediate: true })

const navigators = ref<NavigationModel[]>([])
const isCheckingAuth = ref(true)
const mainStore = useMainStore()
const authStore = useAuthStore()

const isAuthenticated = computed(() => authStore.isAuthenticated)
const isLoggingOut = computed(() => authStore.isLoggingOut)

// Watch for authentication changes and immediately redirect
watch(isAuthenticated, (newValue, oldValue) => {
  if (oldValue === true && newValue === false && !authStore.isLoggingOut) {
    // User just lost authentication (not from our logout process)
    console.log('Authentication lost unexpectedly, redirecting to login...')
    router.push('/auth/signin')
  }
}, { immediate: false })

const handlerInitialize = () => {
  // Sidebar links come from roleBasedSidebarLinks inside LayoutSidebar.
  // Keep legacy prop as empty array for compatibility.
  navigators.value = []
}

const applyTheme = (theme: string) => {
  const root = window.document.documentElement
  root.classList.remove('light', 'dark')
  root.classList.add(theme)
  
  const themeColor = theme === 'dark' ? '#1e1e1e' : '#fff'
  const metaThemeColor = document.querySelector('meta[name="theme-color"]')
  metaThemeColor?.setAttribute('content', themeColor)
}

onBeforeMount(async () => {
  isCheckingAuth.value = true
  
  // Check authentication first - this is critical and should block rendering
  if (!authStore.checkAuth()) {
    isCheckingAuth.value = false
    router.push('/auth/signin')
    return
  }
  
  // Once authenticated, allow page to render immediately
  isCheckingAuth.value = false
})

onMounted(() => {
  handlerInitialize()
  
  const storedTheme = localStorage.getItem('theme')
  if (storedTheme) {
    applyTheme(storedTheme)
  } else {
    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    applyTheme(systemTheme)
  }
})
</script>

<style>
html {
  overflow: hidden;
}
</style>
